import React, { useEffect, useState } from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  Box,
  Flex,
  HeadingOne,
  HeadingThree,
  Link,
  PrimaryButton,
  Label,
  LabelText,
  TextInput,
  VisuallyHidden,
} from '@leagueplatform/genesis-commons';
import { track } from '@leagueplatform/analytics';
import { CHALLENGE_CAMPAIGN_TYPE } from '#constants/analytics.constants.tsx';
import { useGetChallengeByIdData } from '#pages/challenge-landing-page/hooks/use-get-challenge-by-id-data.hook.ts';
import { Formik, Form, Field } from 'formik';
import { BackButtonArrow } from '@leagueplatform/web-common-components';
import { ChooseProfilePicture } from '#components/choose-profile-picture/choose-profile-picture.component.tsx';
import { ErrorWithIcon } from '#components/error-with-icon/error-with-icon.component.tsx';
import {
  JOIN_TEAM_CONTAINER_MAX_WIDTH,
  JOIN_TEAM_PROFILE_PICTURE_SIZE,
  INPUT_MAX_WIDTH,
} from '#pages/join-team-page/constants/join-team-page.constants.ts';
import { CONTENT_MAX_WIDTH } from '#constants/challenges.constants.tsx';
import { useCreateTeam } from '#pages/join-team-page/hooks/use-create-team.hook.ts';
import { TeamPhotoModal } from '../team-photo-modal/team-photo-modal.component';
import { CreateTeamSuccess } from './create-team-success.component';

export interface CreateTeamProps {
  backTo: () => void;
}

interface FormValues {
  teamName: string;
}

export const CreateTeam = ({ backTo }: CreateTeamProps): JSX.Element => {
  const { formatMessage, $tifelse } = useIntl();
  const [teamPhotoUrl, setTeamPhotoUrl] = useState('');
  const [teamPhotoRefId, setTeamPhotoRefId] = useState('');
  const [displayTeamPhotoModal, setDisplayTeamPhotoModal] = useState(false);
  const [displayCreateTeamSuccess, setDisplayCreateTeamSuccess] =
    useState(false);
  const [teamId, setTeamId] = useState('');

  const createTeamMutation = useCreateTeam(
    setTeamId,
    setDisplayCreateTeamSuccess,
  );
  const { data: challengeData } = useGetChallengeByIdData();
  const createNewTeam = (values: FormValues) => {
    createTeamMutation.mutate({
      teamName: values.teamName,
      teamPhoto: teamPhotoRefId,
    });
  };
  useEffect(() => {
    if (challengeData?.data) {
      track('challenges.screen_loaded.create_team', {
        health_campaign_id: challengeData.data.id || '',
        health_campaign_name: challengeData.data.title || '',
        health_campaign_type: CHALLENGE_CAMPAIGN_TYPE,
        challenge_status: challengeData.data.status || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeData]);

  const handleOnClickAvatarLink = () => {
    if (challengeData?.data) {
      track('challenges.button_clicked.create_team.select_team_avatar', {
        health_campaign_id: challengeData.data.id || '',
        health_campaign_name: challengeData.data.title || '',
        health_campaign_type: CHALLENGE_CAMPAIGN_TYPE,
        challenge_status: challengeData.data.status || '',
      });
    }
    setDisplayTeamPhotoModal(true);
  };
  return (
    <Box
      marginX={{ _: 'two', tablet: 'three', desktop: 'auto' }}
      marginY={{ _: 'twoAndHalf', tablet: 'five' }}
      maxWidth={JOIN_TEAM_CONTAINER_MAX_WIDTH}
    >
      {!displayCreateTeamSuccess && (
        <Flex alignItems="center">
          <BackButtonArrow onClick={backTo} />
          <HeadingThree
            as="p"
            marginInlineStart={{ _: 'half', tablet: 'oneAndHalf' }}
          >
            {formatMessage({ id: 'CREATE_A_TEAM' })}
          </HeadingThree>
        </Flex>
      )}
      <VisuallyHidden>
        <HeadingOne>{formatMessage({ id: 'CREATE_A_TEAM' })}</HeadingOne>
      </VisuallyHidden>
      {displayCreateTeamSuccess ? (
        <CreateTeamSuccess teamPhoto={teamPhotoUrl} teamId={teamId} />
      ) : (
        <>
          <Formik
            initialValues={{ teamName: '' }}
            onSubmit={(values) => createNewTeam(values)}
            validate={(values) => {
              const errors: { teamName?: string } = {};
              if (values.teamName === '') {
                errors.teamName = formatMessage({ id: 'TEAM_NAME_ERROR' });
              }
              return errors;
            }}
            validateOnBlur={false}
            validateOnChange={false}
          >
            {({ errors }) => (
              <Form>
                <Flex
                  flexDirection="column"
                  alignItems="center"
                  paddingY={{ _: 'five', tablet: 'four' }}
                  marginX="auto"
                  maxWidth={CONTENT_MAX_WIDTH}
                >
                  <ChooseProfilePicture
                    photoUrl={teamPhotoUrl}
                    size={JOIN_TEAM_PROFILE_PICTURE_SIZE}
                  />
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <Link
                    as="button"
                    type="button"
                    marginY={{ _: 'oneAndHalf', tablet: 'two' }}
                    paddingY="threeQuarters"
                    paddingX="three"
                    backgroundColor="inherit"
                    border="none"
                    onClick={handleOnClickAvatarLink}
                  >
                    {$tifelse(!!teamPhotoUrl, 'CHANGE_AVATAR', 'SELECT_AVATAR')}
                  </Link>
                  <Label
                    marginBottom={{ tablet: 'twoAndHalf' }}
                    width="100%"
                    maxWidth={INPUT_MAX_WIDTH}
                  >
                    <LabelText textAlign="start" marginBottom="quarter">
                      {formatMessage({ id: 'TEAM_NAME_LABEL' })}
                    </LabelText>
                    <Field
                      as={TextInput}
                      name="teamName"
                      type="text"
                      error={errors.teamName}
                    />
                    {errors.teamName && (
                      <ErrorWithIcon errorMessage={errors.teamName} />
                    )}
                  </Label>
                  <PrimaryButton
                    type="submit"
                    marginTop={errors.teamName ? 'oneAndHalf' : 'five'}
                    paddingX="four"
                    isLoading={createTeamMutation.isLoading}
                  >
                    {formatMessage({ id: 'CREATE_TEAM' })}
                  </PrimaryButton>
                </Flex>
              </Form>
            )}
          </Formik>
          <TeamPhotoModal
            displayTeamPhotoModal={displayTeamPhotoModal}
            closeTeamPhotoModal={() => setDisplayTeamPhotoModal(false)}
            setTeamPhotoUrl={setTeamPhotoUrl}
            setTeamPhotoRefId={setTeamPhotoRefId}
          />
        </>
      )}
    </Box>
  );
};
